import {Router} from '@reach/router'
import {graphql} from 'gatsby'
import React from 'react'

import {BillsTracker} from '@/components/bills-tracker'
import SEO from '@/components/seo'
import type {BillsStatusQuery} from '@/graphql-types'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'

interface BillsTrackerProps {
    path: string
    invoiceId?: string
    data: BillsStatusQuery
}

const BillsStatus = (props: BillsTrackerProps) => {
    const content = props.data.allPrismicBillsStatus.nodes[0]?.data

    return (
        <Router basepath="/:lang/bills-status">
            <BillsTracker {...props} content={content} path="/:invoiceId" />
            <BillsTracker {...props} content={content} path="/" />
        </Router>
    )
}

export const Head = ({location, pageContext, data}: GatsbyHeadProps<BillsStatusQuery>) => {
    const pageData = data.allPrismicBillsStatus.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
            preventIndexing={!pageData.allow_indexing}
        />
    )
}

export const query = graphql`
    query BillsStatus($locale: String!) {
        allPrismicBillsStatus(filter: {lang: {eq: $locale}}) {
            nodes {
                data {
                    allow_indexing
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    title
                    lastEdited: last_edited
                    headerCta: header_cta
                    headerCtaLink: header_cta_link {
                        ...PrismicLink
                    }
                    headerLink: header_link {
                        richText
                    }
                    invoiceId: invoice_id
                    company
                    invoiceDate: invoice_date
                    totalAmount: total_amount
                    marketingCard: marketing_card {
                        body {
                            richText
                        }
                        ctaText: cta_text
                        ctaLink: cta_link {
                            ...PrismicLink
                        }
                    }
                    errorContent: error_content {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        errorTitle: error_title
                        ctaText: cta_text
                        ctaLink: cta_link {
                            ...PrismicLink
                        }
                        body {
                            richText
                        }
                    }
                    statusReceived: status_received {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        statusReceivedTitle: status_received_title
                        statusReceivedSubtitle: status_received_subtitle
                    }
                    statusProcessing: status_processing {
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        statusProcessingTitle: status_processing_title
                        statusProcessingSubtitle: status_processing_subtitle
                    }
                    statusPaid: status_paid {
                        statusPaidTitle: status_paid_title
                        statusPaidSubtitle: status_paid_subtitle
                        image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                    }
                    customer_logos_title
                    customer_logos {
                        document {
                            ...CustomerLogos
                        }
                    }
                    questions_title
                    questions {
                        title: question
                        body: answer {
                            richText
                        }
                    }
                    signup_box_title
                    signup_box_placeholder
                    signup_success_message {
                        richText
                    }
                    submit_text
                    accept_terms_error
                    input_aria_label
                }
            }
        }
    }
`

export default BillsStatus
